// Fuel availability dots layer family.
//
// Two symbol layers off the existing 'airports' source, filtered to
// features whose fuelTypes array contains the relevant code. The 100LL
// layer is offset slightly left of the airport icon; Jet-A slightly
// right, so an airport with both shows both badges side-by-side.
//
// Extracted from addOverlays.ts as HI-1 batch 5 round 2 (audit
// 2026-05-22). Pure mechanical move — every layer / filter / icon-
// offset is byte-identical to its pre-extraction form.
//
// Coupling note: this family rides on the `airports` source, which is
// owned by src/map/layers/airports.ts. The mount() function here uses
// the same `map.getSource('airports') && !map.getLayer(...)` guard the
// inline code did, so it's safe to call before OR after airports has
// been added — fuel will just no-op until airports lands. addOverlays.ts
// imports airports before fuel, so mount order matches the historical
// inline order.

import type { Map as MlMap } from 'maplibre-gl';
import { ensureFuelIcons } from '../fuelIcons';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the fuel-availability layers. Idempotent via the !getLayer
 * guard. Requires that the 'airports' source already exist (otherwise
 * the layers can't bind to it); callers should mount airports first.
 */
export function addFuelLayers(map: MlMap): void {
    if (map.getSource('airports') && !map.getLayer('fuel-100ll-symbol')) {
      void ensureFuelIcons(map);
      map.addLayer({
        id: 'fuel-100ll-symbol',
        type: 'symbol',
        source: 'airports',
        minzoom: 7,
        filter: ['in', '100LL', ['coalesce', ['get', 'fuelTypes'], ['literal', []]] as any],
        layout: {
          visibility: 'none',
          'icon-image': 'fuel-100ll',
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            7, 0.65,
            10, 0.95,
            13, 1.15,
          ],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-offset': [-18, 0], // sit left of the airport icon
        },
      });
      map.addLayer({
        id: 'fuel-jeta-symbol',
        type: 'symbol',
        source: 'airports',
        minzoom: 7,
        filter: [
          'any',
          ['in', 'A', ['coalesce', ['get', 'fuelTypes'], ['literal', []]] as any],
          ['in', 'A1', ['coalesce', ['get', 'fuelTypes'], ['literal', []]] as any],
          ['in', 'A++', ['coalesce', ['get', 'fuelTypes'], ['literal', []]] as any],
          ['in', 'J', ['coalesce', ['get', 'fuelTypes'], ['literal', []]] as any],
        ] as any,
        layout: {
          visibility: 'none',
          'icon-image': 'fuel-jeta',
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            7, 0.65,
            10, 0.95,
            13, 1.15,
          ],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-offset': [18, 0],  // sit right of the airport icon
        },
      });
    }
}

registerLayerFamily({
  id: 'fuel',
  label: 'Fuel availability',
  sourceId: 'airports',  // shared with airports
  layerIds: ['fuel-100ll-symbol', 'fuel-jeta-symbol'],
  mount: (map) => addFuelLayers(map),
  // visibilitySelector: family is "on" when either fuel type is toggled on.
  visibilitySelector: (l) => l.fuel100ll || l.fueljeta,
  applyState: (map) => {
    // Two independent toggles: 100LL and Jet-A have separate chips.
    const layers = useLayerVisibilityStore.getState().layers;
    if (map.getLayer('fuel-100ll-symbol')) {
      map.setLayoutProperty('fuel-100ll-symbol', 'visibility', layers.fuel100ll ? 'visible' : 'none');
    }
    if (map.getLayer('fuel-jeta-symbol')) {
      map.setLayoutProperty('fuel-jeta-symbol', 'visibility', layers.fueljeta ? 'visible' : 'none');
    }
  },
});
